import { useState, useMemo } from 'react';
import {
  Page,
  PageTitleWithButton,
  Pagination,
  Button as SMButton,
  Table
} from '../../../../components';
import { Theme, Box, Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Edit, Add } from '@mui/icons-material';
import { Link, useHistory } from 'react-router-dom';
import { ManageModelHomeFurnitureFilters } from './manage-model-home-furniture-filters';
import { getModelHomeFurniture } from '../../../../fetch/possessions';
import { useQuery } from 'react-query';
import { IResponse } from '../../../../models';
import { IModelHomeFurniture } from '../../../../models/possessions';
import { AddPossessionModal } from '../add-possession-modal';

export enum PossessionType {
  ModelHomeFurniture = 1,
  Tools = 2
}

export const ManageModelHomeFurniture = () => {
  const classes = useStyles();
  const history = useHistory();

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    Description?: string;
    Phase?: string;
    Category?: string;
  } | null>({});

  const [selectedSort, setSelectedSort] = useState<string>('Code');
  const [sortDirection, setSortDirection] = useState<{
    Code?: 'Asc' | 'Desc';
    Description?: 'Asc' | 'Desc';
  }>({
    Code: 'Asc'
  });

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const {
    isLoading: isLoadingModelHomeFurniture,
    data: modelHomeFurniture,
    isRefetching: isRefetchingModelHomeFurniture
  } = useQuery<IResponse<IModelHomeFurniture[]>, Error>(
    ['getModelHomeFurniture', page, perPage, filters, selectedSort, sortDirection[selectedSort]],
    async () =>
      await getModelHomeFurniture({
        page: page + 1,
        perPage,
        description: nameSearch,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort]
      }),
    {
      onSuccess: modelHomeFurniture => {
        setRecordCount(modelHomeFurniture.totalRecordCount);
      }
    }
  );

  const modelHomeFurnitureData = useMemo(
    () => modelHomeFurniture?.records ?? [],
    [modelHomeFurniture]
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleEdit = (possessionId: string) => {
    history.push(`/development/possessions/manage-model-home-furniture/${possessionId}`);
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Code',
        accessor: 'code',
        isServerSorted: selectedSort === 'Code',
        isServerSortedDesc: sortDirection.Code === 'Desc',
        handleClickColumn: () => handleClickColumn('Code')
      },
      {
        Header: 'Description',
        accessor: 'description',
        isServerSorted: selectedSort === 'Description',
        isServerSortedDesc: sortDirection.Description === 'Desc',
        handleClickColumn: () => handleClickColumn('Description')
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: any } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <SMButton
                id="edit-county"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => handleEdit(original.possessionId)}
              >
                Edit
              </SMButton>
            </div>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelHomeFurnitureData]);
  return (
    <>
      <Page title="Manage Model Home Furniture">
        <PageTitleWithButton
          pageTitleProps={{
            title: 'Model Home Furniture'
          }}
          button={
            <Box display="flex">
              <Link
                className={classes.link}
                to={`/development/possessions/manage-model-home-furniture`}
              >
                <Button
                  id="add-furniture"
                  startIcon={<Add />}
                  color="primary"
                  variant="contained"
                  className={classes.addFurnitureButton}
                  onClick={() => setModalIsOpen(true)}
                >
                  Add Model Home Furniture
                </Button>
              </Link>
            </Box>
          }
        />
        <ManageModelHomeFurnitureFilters
          isLoading={isLoadingModelHomeFurniture || isRefetchingModelHomeFurniture}
          handleNameSearch={(clearSearch?: boolean) => {
            setPage(0);
            setFilters({
              ...filters,
              Description: nameSearch
            });

            if (clearSearch) {
              setNameSearch('');
            }
          }}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({
                Description: '',
                Phase: '',
                Category: ''
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                Description: nameSearch
              });
            }
          }}
          setPage={setPage}
          setHasAppliedFilters={setHasAppliedFilters}
          searchName={nameSearch}
          setSearchName={setNameSearch}
          hasAppliedFilters={hasAppliedFilters}
        />
        <Table
          columns={columns}
          data={modelHomeFurnitureData}
          isLoading={isLoadingModelHomeFurniture || isRefetchingModelHomeFurniture}
          hidePagination
        />
        {!(isLoadingModelHomeFurniture || isRefetchingModelHomeFurniture) && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Page>
      <AddPossessionModal
        possessionType={PossessionType.ModelHomeFurniture}
        open={modalIsOpen}
        modalIsLoading={false}
        onClose={() => setModalIsOpen(false)}
      />
    </>
  );
};
const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    textAlign: 'right'
  },
  link: {
    textDecoration: 'none'
  },
  addFurnitureButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  }
}));
